import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplet, Clock } from 'lucide-react';
import Icon from '@mdi/react';
import { mdiBee } from '@mdi/js';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { AmazonBanner } from './AdComponents';

export const HeroSection = () => {
  const scrollToGuide = () => {
    const element = document.getElementById('features');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative overflow-hidden bg-cream">
      {/* Top Banner Ad - Amazon Affiliate */}
      <div className="w-full bg-gray-50 flex items-center justify-center py-3">
        <div className="w-full">
          <AmazonBanner
            link="https://amzn.to/46iMYFJ"
            title="Tout l'équipement pour débuter l'apiculture urbaine"
            subTitle="Équipement de qualité pour apiculteurs urbains"
            image="/images/beekeeper-kit.png"
          />
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6 lg:space-y-8">
            <div className="inline-block">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-sage/10 text-sage text-sm font-medium">
                🌱 Guide Complet 2026
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-gray-900 leading-tight">
              L'Apiculture sur <span className="text-honey">Votre Balcon</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
              Apprenez à héberger des abeilles en ville, à respecter la réglementation, 
              et à récolter votre premier miel urbain. Un guide complet pour débutants.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-honey hover:bg-honey/90 text-white font-semibold px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all"
                onClick={scrollToGuide}
              >
                Démarrer le Guide
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Link to="/getting-started">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-sage text-sage hover:bg-sage/90 hover:text-cream hover:border-sage/90 font-semibold px-8 py-6 text-lg transition-all w-full"
                >
                  En Savoir Plus
                </Button>
              </Link>
            </div>

            {/* Stats Cards with Icons */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 pt-8">
              <Card className="border-2 border-honey/20 bg-honey/5 hover:border-honey hover:shadow-lg transition-all">
                <CardContent className="p-3 sm:p-4 text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 rounded-full bg-honey/20 flex items-center justify-center">
                    <Droplet className="h-5 w-5 sm:h-6 sm:w-6 text-honey" />
                  </div>
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-honey mb-1">10-15kg</div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Miel par an</div>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-sage/20 bg-sage/5 hover:border-sage hover:shadow-lg transition-all">
                <CardContent className="p-3 sm:p-4 text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 rounded-full bg-sage/20 flex items-center justify-center">
                    <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-sage" />
                  </div>
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-sage mb-1">15h</div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Entretien/an</div>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-honey/20 bg-honey/5 hover:border-honey hover:shadow-lg transition-all">
                <CardContent className="p-3 sm:p-4 text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 rounded-full bg-honey/20 flex items-center justify-center">
                    <Icon path={mdiBee} size={1} className="sm:hidden" color="#F6C90E" />
                    <Icon path={mdiBee} size={1.5} className="hidden sm:block" color="#F6C90E" />
                  </div>
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-honey mb-1">40,000</div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Abeilles</div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="/images/ruche-balcon.png"
                alt="Apiculture urbaine - ruche sur balcon"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>
            
            {/* Floating Card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center">
                  <span className="text-2xl">✓</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Légal & Sûr</div>
                  <div className="text-sm text-gray-600">Conforme aux normes</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
